import { useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { getLinkStatus } from "./sceneStatus";
import ToolToggle from "./ToolToggle";
import { isOffscreen, useLiveCamera } from "./viewportUtils";
import { fetchApi } from "../../services/api";
import { currentAccount } from "../../proxies";

/**
 * EditorAlerts — the bottom alerts bar of TouchTour Studio (WP-F).
 *
 * It surfaces three derived health counts for the current tour and lets the
 * user fly through the offending scenes:
 *   - Unlinked  — nodes on the board with no navigation hotspots,
 *   - Unplaced  — captured scenes for the event not yet dropped on the board,
 *   - Outside view — board nodes whose screen position is off the visible canvas.
 *
 * It never reaches into TourCanvas internals: off-screen detection projects each
 * node with the shared viewportUtils helpers (the same maths as
 * TourCanvas.getPosition) using the live camera read from the Canvas API
 * (getCameraState). Individual alert chips cycle through their offenders via
 * jumpTo; "View all" frames every pannable offender at once (fitToImages).
 */

const EditorAlerts = ({ canvasApiRef, images, onSelectImage, tool, onChangeTool }) => {
  const classes = useStyles();
  const { event } = useSnapshot(currentAccount);

  const [scenes, setScenes] = useState([]);
  const camera = useLiveCamera(canvasApiRef);

  // Remembers where we are in a "fly through the offenders" cycle so repeated
  // clicks advance to the next one. The snapshot of ids is frozen for the pass
  // because jumping changes which nodes are off-screen.
  const cycleRef = useRef({ key: null, ids: [], index: 0 });

  // Same /images/list/{event} fetch the ScenesPanel uses, so "unplaced" here
  // matches the panel's Unplaced tab exactly (captured scenes not on the board).
  useEffect(() => {
    if (event === null) {
      setScenes([]);
      return undefined;
    }

    let cancelled = false;
    fetchApi(`/images/list/${event}`).then(items => {
      if (cancelled) return;
      setScenes(
        (Array.isArray(items) ? items : []).map(item => ({
          id: item.id.toString(),
        })),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [event]);

  const boardImages = useMemo(() => Object.values(images ?? {}), [images]);

  const unlinked = useMemo(
    () => boardImages.filter(image => getLinkStatus(image) === "unlinked"),
    [boardImages],
  );

  const placedIds = useMemo(
    () => new Set(Object.keys(images ?? {})),
    [images],
  );
  const unplacedCount = useMemo(
    () => scenes.filter(scene => !placedIds.has(scene.id)).length,
    [scenes, placedIds],
  );

  const outsideView = useMemo(
    () => boardImages.filter(image => isOffscreen(image, camera)),
    [boardImages, camera],
  );

  const callApi = (method, ...args) => {
    const api = canvasApiRef && canvasApiRef.current;
    if (api && typeof api[method] === "function") {
      api[method](...args);
    }
  };

  // Advance the camera to the next offending node for `key`. The first click on
  // a key snapshots the current offenders; subsequent clicks step through them.
  const cycle = (key, offenders) => {
    if (!offenders || offenders.length === 0) return;

    const state = cycleRef.current;
    let ids;
    let index;
    if (state.key === key && state.index + 1 < state.ids.length) {
      ids = state.ids;
      index = state.index + 1;
    } else {
      ids = offenders.map(node => node.id);
      index = 0;
    }

    cycleRef.current = { key, ids, index };

    const node = (images ?? {})[ids[index]];
    if (!node) return;

    callApi("jumpTo", node.x, node.y);
    if (onSelectImage) onSelectImage(node.id);
  };

  // "View all" frames the union of pannable offenders (off-screen + unlinked);
  // unplaced scenes aren't on the board so they can't be flown to.
  const pannableOffenders = useMemo(() => {
    const seen = new Set();
    return [...outsideView, ...unlinked].filter(node => {
      if (seen.has(node.id)) return false;
      seen.add(node.id);
      return true;
    });
  }, [outsideView, unlinked]);

  const viewAll = () => {
    if (pannableOffenders.length === 0) return;
    callApi(
      "fitToImages",
      pannableOffenders.map(node => node.id),
    );
  };

  const alerts = [
    {
      key: "unlinked",
      icon: ["fal", "link-slash"],
      count: unlinked.length,
      label: "Unlinked scenes",
      tone: classes.toneUnlinked,
      offenders: unlinked,
    },
    {
      key: "unplaced",
      icon: ["fal", "images"],
      count: unplacedCount,
      label: "Unplaced scenes",
      tone: classes.toneUnplaced,
      offenders: null,
    },
    {
      key: "outside",
      icon: ["fal", "eye-slash"],
      count: outsideView.length,
      label: "Scenes outside view",
      tone: classes.toneOutside,
      offenders: outsideView,
    },
  ];

  return (
    <div className={classes.container}>
      <span className={classes.title}>Editor alerts</span>

      <div className={classes.alerts}>
        {alerts.map(alert => {
          const clickable = alert.offenders && alert.offenders.length > 0;
          return (
            <button
              key={alert.key}
              type="button"
              className={clsx(classes.alert, alert.tone, {
                [classes.alertMuted]: alert.count === 0,
                [classes.alertClickable]: clickable,
              })}
              onClick={
                clickable ? () => cycle(alert.key, alert.offenders) : undefined
              }
              disabled={!clickable}
              title={clickable ? "Click to fly through these scenes" : undefined}
            >
              <FontAwesomeIcon icon={alert.icon} className={classes.icon} />
              <span className={classes.count}>{alert.count}</span>
              <span className={classes.label}>{alert.label}</span>
            </button>
          );
        })}
      </div>

      <div className={classes.toolCenter}>
        <ToolToggle tool={tool} onChange={onChangeTool} />
      </div>

      <button
        type="button"
        className={classes.viewAll}
        onClick={viewAll}
        disabled={pannableOffenders.length === 0}
      >
        View all
      </button>
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  container: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing3,
    width: "100%",
    boxSizing: "border-box",
    padding: [theme.spacing2, theme.spacing3],
    backgroundColor: theme.colorBlack,
    borderTop: `1px solid ${theme.colorDarkGrey}`,
  },
  title: {
    flex: "0 0 auto",
    color: theme.colorGrey,
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  alerts: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing3,
    flexWrap: "wrap",
  },
  alert: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: [4, 8],
    margin: 0,
    backgroundColor: "transparent",
    border: "1px solid transparent",
    borderRadius: theme.borderRadius,
    color: theme.textColor,
    fontSize: 12,
    fontFamily: "inherit",
    cursor: "default",
    transitionProperty: "border-color, background-color, opacity",
    transitionDuration: theme.transitionDuration,
  },
  alertClickable: {
    cursor: "pointer",
    "&:hover": {
      borderColor: theme.colorDarkGrey,
      backgroundColor: theme.colorBackground,
    },
  },
  alertMuted: {
    opacity: 0.45,
  },
  icon: {
    fontSize: 13,
  },
  count: {
    fontWeight: 900,
    fontVariantNumeric: "tabular-nums",
  },
  label: {
    color: theme.colorLightGrey,
  },
  toneUnlinked: {
    "& $icon, & $count": {
      color: theme.colorSecondary,
    },
  },
  toneUnplaced: {
    "& $icon, & $count": {
      color: theme.colorRed,
    },
  },
  toneOutside: {
    "& $icon, & $count": {
      color: theme.colorPrimary,
    },
  },
  toolCenter: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "auto",
  },
  viewAll: {
    flex: "0 0 auto",
    marginLeft: "auto",
    boxSizing: "border-box",
    padding: [6, 14],
    backgroundColor: "transparent",
    color: theme.textColor,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    fontSize: 12,
    fontFamily: "inherit",
    cursor: "pointer",
    transitionProperty: "color, border-color",
    transitionDuration: theme.transitionDuration,
    "&:hover:not(:disabled)": {
      color: theme.colorPrimary,
      borderColor: theme.colorPrimary,
    },
    "&:disabled": {
      opacity: 0.4,
      cursor: "default",
    },
  },
}));

export default EditorAlerts;
